/**
 * Hook that wraps useItems and applies milestone filtering logic
 * Adds hasHighlightedMilestone flag to items based on activeMilestoneTypes filter
 */

import { useMemo, useEffect } from 'react';
import { useItems } from './useItems';
import { useItemsMilestones } from './milestones/useItemMilestones';
import { globalFeatureFlags } from '@/lib/featureFlags';
import { logger } from '@/lib/logger';
import { kpiTracker } from '@/lib/kpiTracking';
import { itemTypeMonitor } from '@/lib/monitoring/itemTypeMetrics';
import { ItemFilters, TimelineItem } from '@/types/item';
import { calculateItemCompletion } from '@/utils/itemCompletion';

interface UseItemsWithMilestoneFilterOptions {
  projectId?: string;
  allowedProjectIds?: string[];
  searchTerm?: string;
  category?: string;
  filters?: ItemFilters;
  itemType?: 'build' | 'service'; // ✅ undefined = no item_type filter
}

export const useItemsWithMilestoneFilter = ({
  projectId,
  allowedProjectIds,
  searchTerm,
  category,
  filters,
  itemType // ✅ No default - pass through as-is (can be undefined)
}: UseItemsWithMilestoneFilterOptions) => {
  // Fetch items using the original hook with item_type filter
  const itemsQuery = useItems(projectId, allowedProjectIds, searchTerm, category, itemType);
  
  // ✅ HARDCODED (2025-11-09): Milestone feature always enabled
  const milestoneFeatureEnabled = true;
  
  // Fetch milestones if feature is enabled and we have items
  const itemIds = itemsQuery.data?.map(item => item.id) || [];
  const milestonesQuery = useItemsMilestones(
    milestoneFeatureEnabled ? itemIds : [],
    { enabled: milestoneFeatureEnabled && itemIds.length > 0 }
  );
  
  // Group milestones by item (memoized separately for optimization)
  const milestonesByItem = useMemo(() => {
    const milestones = milestonesQuery.data || [];
    if (!milestoneFeatureEnabled || milestones.length === 0) {
      return {};
    }
    return milestones.reduce((acc, m) => {
      if (!acc[m.item_id]) acc[m.item_id] = [];
      acc[m.item_id].push(m);
      return acc;
    }, {} as Record<string, typeof milestones>);
  }, [milestonesQuery.data, milestoneFeatureEnabled]);

  // Apply milestone filtering - ALWAYS calculate completion_percentage
  const filteredItems = useMemo(() => {
    const baseItems = itemsQuery.data || [];
    
    // ✅ CLIENT-SIDE FILTERING: Filter by item_type if specified
    const items = itemType
      ? baseItems.filter(item => {
          const itemItemType = item.itemType ?? 'build'; // NULL maps to 'build'
          return itemItemType === itemType;
        })
      : baseItems;
    
    // Check for active filters
    const hasTypeFilter = filters?.activeMilestoneTypes && filters.activeMilestoneTypes.length > 0;
    const hasNameFilter = filters?.milestoneName && filters.milestoneName.trim();
    const hasActiveFilters = hasTypeFilter || hasNameFilter;
    
    const startTime = performance.now();
    
    // ✅ Track query start for item_type monitoring
    const queryStartTime = performance.now();
    
    // ✅ NEW: Filter by search term in milestoneName (checks both item name and milestone names)
    const searchTerm = filters?.milestoneName?.trim().toLowerCase();
    
    // CRITICAL: ALWAYS calculate completion_percentage for ALL items
    const result = items
      // ✅ STEP 1: Filter items by name OR milestone name
      .filter(item => {
        // No search term = show all
        if (!searchTerm) return true;
        
        // Check if item name matches
        const itemNameMatch = item.name.toLowerCase().includes(searchTerm);
        if (itemNameMatch) return true;
        
        // Check if any milestone name matches
        const itemMilestones = milestonesByItem[item.id] || [];
        const milestoneNameMatch = itemMilestones.some(m => 
          m.name?.toLowerCase().includes(searchTerm)
        );
        
        return milestoneNameMatch;
      })
      // ✅ STEP 2: Map to add completion % and highlight flag
      .map(item => {
        const itemMilestones = milestonesByItem[item.id] || [];
        
        // Only apply highlighting logic if filters are active
        const hasMatchingMilestone = milestoneFeatureEnabled && hasActiveFilters
          ? itemMilestones.some(m => {
              // Type filter
              const typeMatch = !hasTypeFilter || 
                filters.activeMilestoneTypes!.includes(m.milestone_type || 'default');
              
              // Name filter
              const nameMatch = !hasNameFilter || (
                m.name && 
                m.name.toLowerCase().includes(filters.milestoneName!.trim().toLowerCase())
              );
              
              // Both conditions must match (AND logic)
              return typeMatch && nameMatch;
            })
          : false; // No filters = no highlighting
        
        // ALWAYS calculate completion % (3-tier priority: manual > milestone > time-based)
        const completionPercentage = calculateItemCompletion(
          item, 
          itemMilestones as any,
          item.manual_completion_percentage
        );
        
        return {
          ...item,
          hasHighlightedMilestone: hasMatchingMilestone,
          completion_percentage: completionPercentage // Always present!
        } as TimelineItem;
      });
    
    const filterTime = performance.now() - startTime;
    const queryTime = performance.now() - queryStartTime;
    
    // ✅ NEW: Track item_type filter performance
    if (milestoneFeatureEnabled) {
      itemTypeMonitor.trackFilter({
        filteredItemsCount: result.length,
        queryTime,
        itemType: itemType || 'build',
        timestamp: Date.now()
      });
    }
    
    if (milestoneFeatureEnabled && hasActiveFilters) {
      kpiTracker.recordKPI('ENABLE_CUSTOM_MILESTONES', 'milestone_filter_time', filterTime);
      
      logger.debug('useItemsWithMilestoneFilter', 'Milestone filter applied', {
        totalItems: result.length,
        highlightedItems: result.filter(i => i.hasHighlightedMilestone).length,
        activeMilestoneTypes: filters.activeMilestoneTypes,
        milestoneName: filters.milestoneName,
        filterTimeMs: Math.round(filterTime)
      });
    }
    
    return result;
  }, [itemsQuery.data, milestonesByItem, filters?.activeMilestoneTypes, filters?.milestoneName, milestoneFeatureEnabled, itemType]);
  
  // Track performance when active
  useEffect(() => {
    if (milestoneFeatureEnabled && filters?.activeMilestoneTypes?.length) {
      const threshold = { 
        metric: 'milestone_filter_time',
        threshold: 100, 
        operator: 'lt' as const, 
        description: 'Milestone filter should complete under 100ms' 
      };
      const isWithinThreshold = kpiTracker.checkThreshold('ENABLE_CUSTOM_MILESTONES', threshold);
      
      if (!isWithinThreshold) {
        logger.warn('useItemsWithMilestoneFilter', 'Milestone filter time exceeds threshold', {
          threshold: threshold.threshold,
          averageTime: kpiTracker.getAverage('ENABLE_CUSTOM_MILESTONES', 'milestone_filter_time')
        });
      }
    }
  }, [filters?.activeMilestoneTypes, milestoneFeatureEnabled]);
  
  return {
    ...itemsQuery,
    data: filteredItems
  };
};
